import { normalizeOperationOutcome } from '@medplum/core'
import type { QuestionnaireItem } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import {
  type AnswerScaffoldLabel,
  enumerateAnswerLabels,
} from '@seen/core/models/care-plans/answerScaffold'
import { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import {
  type FieldDecisionStatus,
  type PersistedQuestionnaireAnswerOp,
  questionnaireAnswerKey,
} from '@seen/core/models/compositionNote'
import type {
  ExtendedQuestionnaireResponse,
  QuestionnaireWithResponse,
} from '@seen/core/models/questionnaireResponse'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  acceptedAnswerValue,
  answerValueForItem,
  applyBulkAccept,
  type BulkAcceptItemWrite,
  type BulkAcceptResult,
  hasValue,
  planBulkAcceptReversal,
  type UnplaceableAtomReason,
  unplaceableQuestionnaireAtoms,
} from '#src/components/care-plan/answerAccept.ts'
import {
  substituteLineValue,
  toInlineValue,
} from '#src/components/care-plan/answerLabelLines.ts'
import {
  ANSWER_CHANGED_REASON,
  type AtomAnswerSnapshot,
  planAnswerReversal,
  snapshotAnswerAtom,
  toAnswerValue,
} from '#src/components/care-plan/answerUndo.ts'
import { parseAnswerHtml } from '#src/components/care-plan/parseAnswerHtml.ts'
import { useSuggestionReveal } from '#src/components/rich-text-editor/SuggestionRevealContext.tsx'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useAIAssistantStore } from '#src/hooks/useAIAssistantStore.ts'
import {
  type DroppedQuestionnaireAtom,
  droppedQuestionnaireAtomId,
  publishDroppedQuestionnaireAtoms,
  withdrawDroppedQuestionnaireAtoms,
} from '#src/hooks/useDroppedQuestionnaireAtomsStore.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import {
  type UsePatchMutationProxy,
  usePatchMutation,
} from '#src/hooks/usePatchMutation.ts'
import { useQuestionnaireDecisionUndoHotkey } from '#src/hooks/useQuestionnaireDecisionUndoHotkey.ts'
import {
  type AcceptedAtomEntry,
  claimDecisionReversal,
  type QuestionnaireDecisionEntry,
  registerQuestionnaireDecisionExecutor,
  releaseDecisionReversal,
  useQuestionnaireDecisionUndoStore,
} from '#src/hooks/useQuestionnaireDecisionUndoStore.ts'
import { useRead } from '#src/hooks/useRead.ts'
import {
  useStreamingQuestionnaireSuggestion,
  useStreamingQuestionnaireSuggestionStore,
} from '#src/hooks/useStreamingQuestionnaireSuggestionStore.ts'
import { useSuggestionPersistence } from '#src/hooks/useSuggestionPersistence.ts'
import { extractLinesFromHTML } from '#src/utils/html.ts'

export type SuggestedAnswer = PersistedQuestionnaireAnswerOp['answers'][number]

/* A clinician's hand edit of one rendered line of an item's answer — the lines
 * the suggestion surface shows around its cards, which are the only way to
 * touch the field while any atom of the item is still undecided. */
export interface AnswerLineEdit {
  // The line's index into the answer's lines, as the surface read them.
  index: number
  // That line as it was rendered, which the write checks the index still holds.
  previousHtml: string
  // The island's HTML, block-wrapped as an editor document always is.
  html: string
}

export interface CarePlanQuestionnaireSuggestionState {
  /* Undecided suggested answers, grouped by the item they belong to. One item
   * carries one atom per scaffold label it proposes a value for, in the order
   * the staged record lists them, which follows the scaffold. Empty whenever
   * the suggestion surface should not appear at all. */
  answersByLinkId: ReadonlyMap<string, SuggestedAnswer[]>
  // False while the response is still being created by the page's own query.
  // The proposal shows, but accepting is blocked — creating the response here
  // would race that create and could duplicate it.
  canAccept: boolean
  /* The atom is the unit of decision, so both take the answer itself.
   * `editedHtml` is the clinician's island edit, when they made one; without it
   * the AI's answer is written as proposed. */
  accept: (answer: SuggestedAnswer, editedHtml?: string) => void
  reject: (answer: SuggestedAnswer) => void
  /* Writes one hand-edited line of an item's answer. No decision is involved —
   * the line carries no proposal — so nothing is recorded against the staged
   * record and the atoms still pending are left exactly as they were. */
  editLine: (linkId: string, edit: AnswerLineEdit) => void
}

/* Said instead of the generic "try again" when the response moved under the
 * write. A 412 is not a transient failure: another clinician (or the plan's own
 * sync) put different text in this field, and repeating the same accept would
 * either fail the same way or bury their work. */
export const ANSWER_CHANGED_ELSEWHERE =
  "Someone else changed this answer. It's been refreshed — check the updated text before accepting."

/* Said instead of reversing an answer that has nowhere to come back to. An undo
 * that wrote the compensating patch anyway would take the text out of the care
 * plan and leave no card to accept it again, which is worse than not undoing. */
export const ANSWER_UNDO_UNAVAILABLE =
  'That suggestion is no longer on this care plan, so it cannot be undone here.'

// The same nowhere-to-land refusal, said on the redo side.
export const ANSWER_REDO_UNAVAILABLE =
  'That suggestion is no longer on this care plan, so it cannot be redone here.'

// Bounded so a pathological (or self-referential) `cause` chain cannot spin.
const MAX_CAUSE_DEPTH = 8

/* Whether a failed answer write failed its optimistic-concurrency test. The
 * client rethrows the 412 raw when it cannot retry, and wraps it — its own
 * message, the outcome on `cause` — when its inline retry gives up or judges the
 * concurrent change unsafe to reapply, so the whole chain is walked. */
export function isAnswerVersionConflict(cause: unknown): boolean {
  let node: unknown = cause
  for (let depth = 0; node !== undefined && node !== null; depth++) {
    if (depth >= MAX_CAUSE_DEPTH) {
      return false
    }
    if (isVersionTestFailure(node)) {
      return true
    }
    const inner =
      typeof node === 'object' && 'cause' in node
        ? (node as { cause: unknown }).cause
        : undefined
    node = inner === node ? undefined : inner
  }
  return false
}

function isVersionTestFailure(cause: unknown): boolean {
  try {
    const outcome = normalizeOperationOutcome(cause)
    return Boolean(
      outcome.issue?.some(
        (issue) =>
          issue.code === 'invalid' &&
          /^test failed/i.test(issue.details?.text ?? '')
      )
    )
  } catch {
    return false
  }
}

/* Derived here rather than borrowed from `deriveRecordStatusFromFieldDecisions`,
 * which never returns 'accepted' by design: a validated tool only commits when
 * its form is submitted, so an all-accepted record of that kind is still
 * pending. A questionnaire answer has no submit step — accepting one patches the
 * response immediately — so once every answer is decided the record is finished,
 * and leaving it pending would keep a settled suggestion in the pending list. */
function deriveQuestionnaireRecordStatus(
  atomKeys: string[],
  decisions: Record<string, FieldDecisionStatus> | undefined
): 'pending' | 'accepted' | 'rejected' {
  if (
    atomKeys.length === 0 ||
    atomKeys.some((key) => (decisions?.[key] ?? 'pending') === 'pending')
  ) {
    return 'pending'
  }
  return atomKeys.every((key) => decisions?.[key] === 'rejected')
    ? 'rejected'
    : 'accepted'
}

/* Where an atom whose labelled line has gone from the answer ends up.
 *
 * True: it is listed with the other suggestions that couldn't be placed, and
 * stays pending until the clinician dismisses it. False: it is decided
 * REJECTED on sight, with nothing shown — the verdict staging would have
 * reached had the line already been missing, since an answer the participant's
 * form has no line for is arguably not a suggestion at all.
 *
 * It is listed. Unplaceable questionnaire suggestions go to the same
 * couldn't-be-placed list as narrative ones, so the clinician sees the AI's
 * work and dismisses it rather than it vanishing silently — and the record
 * stays pending until they do, as a narrative record does. */
const LIST_ATOMS_WHOSE_LINE_IS_GONE = true

/* Whether an unplaceable atom of this kind is listed, or decided REJECTED on
 * sight instead. Listing needs the list to open (`ai-note-suggestions`): a row
 * nobody can dismiss would hold its record open for good, with no card to act
 * on and no bulk control counting it. So where it cannot open, every kind is
 * settled on sight, as an atom whose line had gone always used to be. */
function listsUnplaceable(
  reason: UnplaceableAtomReason,
  droppedListOpens: boolean
): boolean {
  return (
    droppedListOpens &&
    (reason !== 'lineMissing' || LIST_ATOMS_WHOSE_LINE_IS_GONE)
  )
}

// What the couldn't-be-placed dialog says about each kind of unplaceable atom.
const UNPLACEABLE_REASON_TEXT: Record<UnplaceableAtomReason, string> = {
  targetChanged:
    'This line was filled in after the AI made the suggestion, and accepting it would overwrite what is there.',
  lineMissing: 'The line this was written for is no longer in the answer.',
  lineTaken: 'Another suggestion already fills this line.',
}

/* The row's caption: the question, then the line it targets. A label is copied
 * with its trailing colon, which reads as clutter once it is no longer in front
 * of a value. */
function droppedAtomLabel(
  item: QuestionnaireItem | undefined,
  answer: SuggestedAnswer
): string {
  const bare = (text: string | undefined): string =>
    (text ?? '').trim().replace(/:\s*$/, '')
  const question = bare(item?.text) || answer.linkId
  const line = [bare(answer.section), bare(answer.label)]
    .filter((part) => part !== '')
    .join(' — ')
  return line === '' ? question : `${question}: ${line}`
}

// The proposed value as prose, for the row's text and its copy control.
function droppedAtomContent(
  item: QuestionnaireItem | undefined,
  answer: SuggestedAnswer
): string | undefined {
  const value = answerValueForItem(answer, item)
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  const text = value === undefined ? '' : extractLinesFromHTML(value)
  return text === '' ? undefined : text
}

/* The AI's staged answers for one care-plan demographic questionnaire, resolved
 * per item and, within an item, per scaffold label, so every atomic edit
 * renders and is decided on its own.
 *
 * Nothing here writes to the QuestionnaireResponse until a clinician accepts:
 * the proposal lives in the per-practitioner staging `Basic`, and accept is the
 * only path that patches the response.
 *
 * Every decision is also recorded on the tab-wide undo history, because an
 * accept is a FHIR write the editor's UndoManager cannot see; reversing one is
 * a compensating patch planned in `answerUndo.ts`.
 *
 * Read from context (the assistant store's `resourceRef` plus the staging
 * record) rather than through props, so no new prop crosses the questionnaire
 * node view's memo boundary. */
export function useCarePlanQuestionnaireSuggestion(
  questionnaire: QuestionnaireWithResponse['questionnaire'],
  questionnaireResponse: QuestionnaireWithResponse['questionnaireResponse']
): CarePlanQuestionnaireSuggestionState {
  const isReadOnly = useIsReadOnly()
  const droppedListOpens = useFeatureFlag(featureFlags.aiNoteSuggestions)
  const resourceRef = useAIAssistantStore((state) => state.resourceRef)
  const persistence = useSuggestionPersistence(resourceRef ?? undefined)
  const questionnaireUrl = questionnaire.url ?? ''
  const record = persistence.getPendingQuestionnaireSuggestion(questionnaireUrl)
  const {
    projectFieldDecisionsWithStatus,
    canRependQuestionnaireAnswer,
    rependQuestionnaireAnswer,
  } = persistence

  /* The answers of an in-flight `proposeQuestionnaireAnswers` call whose JSON
   * has closed so far. A tool call is atomic, so without this the clinician
   * waits out the whole generation and then gets every card at once. */
  const streaming = useStreamingQuestionnaireSuggestion(questionnaireUrl)
  const clearStreamingAnswers = useStreamingQuestionnaireSuggestionStore(
    (state) => state.clearStreamingAnswers
  )
  /* Hand the call over from its streamed prefix to its persisted record in the
   * render the record first appears in, so no card blinks out between the two.
   * Dropping the streamed copy HERE rather than when the run ends is also what
   * keeps a settled record from coming back: the streamed answers are gone
   * before any of them can be decided.
   */
  useEffect(() => {
    if (streaming && record?.provenance.toolCallId === streaming.toolCallId) {
      clearStreamingAnswers(streaming.toolCallId)
    }
  }, [streaming, record, clearStreamingAnswers])

  // Suggestions hide and show with the SAME reveal toggle as the inline editor
  // proposals, so one control governs every suggestion on the page.
  const { revealed } = useSuggestionReveal()

  const recordDecision = useQuestionnaireDecisionUndoStore(
    (state) => state.recordDecision
  )
  const commitUndo = useQuestionnaireDecisionUndoStore(
    (state) => state.commitUndo
  )
  const commitRedo = useQuestionnaireDecisionUndoStore(
    (state) => state.commitRedo
  )
  /* Cmd+Z is the whole affordance — there is no button, matching how the
   * editor's own suggestions already behave. The shortcut is live only while a
   * questionnaire that can be decided on is mounted. */
  useQuestionnaireDecisionUndoHotkey(!isReadOnly)

  /* Keyed on `isEditable`, NOT on `supportingQuestionnaireResponses` being
   * non-empty — that is populated when a plan is created, so it is true for new
   * drafts and says nothing about whether answers can be written. Undefined
   * while the read is in flight, so only a resolved non-editable plan hides the
   * suggestions. */
  const { data: carePlan } = useRead(InitialCarePlan, resourceRef)
  const isFrozen = carePlan?.isEditable === false

  /* A write failure says which KIND of failure it was. A version conflict is
   * not "try again": the answer has moved, so the response is refetched and the
   * card re-diffs against the text that is actually there. Everything else —
   * network, permissions — keeps the wording each write already had, which
   * differs per write because "the suggestion is still here to accept again"
   * says nothing about text the clinician typed themselves. */
  const queryClient = useQueryClient()
  const onWriteError = useCallback(
    (transportMessage: string) =>
      (
        cause: unknown,
        variables: UsePatchMutationProxy<ExtendedQuestionnaireResponse>
      ): void => {
        if (isAnswerVersionConflict(cause)) {
          cacheKeys.invalidateMutation(
            queryClient,
            variables.instance.fhirType,
            variables.instance.id
          )
          toast.error(ANSWER_CHANGED_ELSEWHERE)
          return
        }
        toast.error(transportMessage)
      },
    [queryClient]
  )

  /* The record is only marked decided once the write has landed. Doing it
   * eagerly would consume the proposal on a failed PATCH (version conflict,
   * permissions, network), leaving nothing written and no way back: the pending
   * selector filters on `status === 'pending'`, and the flip is persisted. */
  const mutate = usePatchMutation<ExtendedQuestionnaireResponse>({
    mutation: {
      onError: onWriteError(
        'Could not save the suggested answer. The suggestion is still here — try accepting again.'
      ),
    },
  }).mutate

  /* A third mutation so a failed hand edit reports itself as one: the accept
   * message tells the clinician their suggestion is still there to accept
   * again, which says nothing about text they typed themselves. */
  const editMutate = usePatchMutation<ExtendedQuestionnaireResponse>({
    mutation: {
      onError: onWriteError(
        'Could not save your edit. Nothing has been changed.'
      ),
    },
  }).mutate

  // A separate mutation so a failed reversal reports itself as one; its record
  // stays on the undo history, since nothing was written.
  const reverseMutation = usePatchMutation<ExtendedQuestionnaireResponse>({
    mutation: {
      onError: onWriteError(
        'Could not undo that answer. Nothing has been changed.'
      ),
    },
  })
  const reverseMutate = reverseMutation.mutate
  const reverseMutateAsync = reverseMutation.mutateAsync

  // The questionnaire's items by linkId, which every path that reads or writes
  // an answer resolves against: the item's type is what picks an answer's value.
  const itemsByLinkId = useMemo(() => {
    const byLinkId = new Map<string, QuestionnaireItem>()
    for (const item of questionnaire.item ?? []) {
      if (item.linkId) {
        byLinkId.set(item.linkId, item)
      }
    }
    return byLinkId
  }, [questionnaire])

  /* The staged record once the call has written one, and until then whatever
   * of that same call has streamed in. The record persists this call's answers
   * in the same order, after any atoms carried forward off the run it
   * superseded, so the handover only ever adds cards — it never moves,
   * duplicates or removes one. */
  const undecidedByLinkId = useMemo(() => {
    const byLinkId = new Map<string, SuggestedAnswer[]>()
    const proposed = record?.op.answers ?? streaming?.answers
    if (!proposed) {
      return byLinkId
    }
    for (const answer of proposed) {
      /* An atom already decided drops out; its line renders as ordinary text
       * again while its undecided siblings stay suggested. Streamed answers
       * carry no decisions — nothing is decidable before the record exists. */
      const decision =
        record?.fieldDecisions?.[questionnaireAnswerKey(answer)] ?? 'pending'
      if (
        !hasValue(answer, itemsByLinkId.get(answer.linkId)) ||
        decision !== 'pending'
      ) {
        continue
      }
      const atoms = byLinkId.get(answer.linkId)
      if (atoms) {
        atoms.push(answer)
      } else {
        byLinkId.set(answer.linkId, [answer])
      }
    }
    return byLinkId
  }, [itemsByLinkId, record, streaming])

  /* The undecided atoms the answer, as it stands now, has nowhere to put —
   * judged live, so an atom comes back as a card the moment its line can take
   * it again. Only ever with a real answer in hand: `undefined` is a response
   * that has not loaded, and reading that as "every line is gone" would list
   * every atom of a slow read. */
  const unplaceable = useMemo(() => {
    const byKey = new Map<string, UnplaceableAtomReason>()
    if (!questionnaireResponse) {
      return byKey
    }
    for (const [linkId, atoms] of undecidedByLinkId) {
      const held = unplaceableQuestionnaireAtoms(
        questionnaireUrl,
        questionnaireResponse.getAnswerValue(linkId),
        atoms
      )
      for (const [key, reason] of held) {
        byKey.set(key, reason)
      }
    }
    return byKey
  }, [undecidedByLinkId, questionnaireResponse, questionnaireUrl])

  // The atoms that render as cards: everything undecided that can be placed.
  const answersByLinkId = useMemo(() => {
    const byLinkId = new Map<string, SuggestedAnswer[]>()
    if (isReadOnly || isFrozen || !revealed) {
      return byLinkId
    }
    for (const [linkId, atoms] of undecidedByLinkId) {
      const placed = atoms.filter(
        (answer) => !unplaceable.has(questionnaireAnswerKey(answer))
      )
      if (placed.length > 0) {
        byLinkId.set(linkId, placed)
      }
    }
    return byLinkId
  }, [isReadOnly, isFrozen, revealed, undecidedByLinkId, unplaceable])

  /* Record one or more atom decisions and, in the SAME version-checked PATCH,
   * the record status they derive: once every ATOM is decided the record itself
   * is settled, so it stops being pending and the suggestion surface
   * disappears. An item with several atoms therefore keeps the record pending
   * until its last label is decided.
   *
   * A decision and the status derived from it describe one clinician action, so
   * they ride ONE write (`projectFieldDecisionsWithStatus`). Issued as two, one
   * could land without the other and leave the record disagreeing with its own
   * field decisions: a settled status over atoms that still read undecided, or
   * saved decisions under a status that still reads pending, which keeps the
   * banner counting work that is already done. */
  const decideAtoms = useCallback(
    (
      decisions: readonly { atomKey: string; status: 'accepted' | 'rejected' }[]
    ): void => {
      if (!record || decisions.length === 0) {
        return
      }
      const nextDecisions = { ...record.fieldDecisions }
      for (const decision of decisions) {
        nextDecisions[decision.atomKey] = decision.status
      }
      const recordStatus = deriveQuestionnaireRecordStatus(
        record.op.answers
          .filter((answer) =>
            hasValue(answer, itemsByLinkId.get(answer.linkId))
          )
          .map(questionnaireAnswerKey),
        nextDecisions
      )
      void projectFieldDecisionsWithStatus(
        decisions.map((decision) => ({
          id: record.id,
          atomKey: decision.atomKey,
          status: decision.status,
        })),
        /* A record with atoms still undecided keeps the status it has: a
         * decision never derives a flip BACK to pending, and passing one would
         * re-open a record another tab has just settled. */
        recordStatus === 'pending'
          ? undefined
          : { id: record.id, status: recordStatus }
      )
    },
    [itemsByLinkId, record, projectFieldDecisionsWithStatus]
  )

  /* The unplaceable atoms of the staged record, as rows of the editor's
   * couldn't-be-placed list. Only once the record exists, since a dismissal is
   * a decision recorded against it, and only while the cards themselves would
   * show. An atom of a kind that is not listed (`listsUnplaceable`) is
   * settled below instead. */
  const droppedRows = useMemo((): DroppedQuestionnaireAtom[] => {
    if (!record || isReadOnly || isFrozen || !revealed) {
      return []
    }
    const rows: DroppedQuestionnaireAtom[] = []
    for (const answer of record.op.answers) {
      const atomKey = questionnaireAnswerKey(answer)
      const reason = unplaceable.get(atomKey)
      if (reason === undefined || !listsUnplaceable(reason, droppedListOpens)) {
        continue
      }
      const item = itemsByLinkId.get(answer.linkId)
      rows.push({
        id: droppedQuestionnaireAtomId(record.id, atomKey),
        questionnaireUrl,
        recordId: record.id,
        atomKey,
        label: droppedAtomLabel(item, answer),
        reason: UNPLACEABLE_REASON_TEXT[reason],
        content: droppedAtomContent(item, answer),
      })
    }
    return rows
  }, [
    record,
    isReadOnly,
    isFrozen,
    revealed,
    unplaceable,
    itemsByLinkId,
    questionnaireUrl,
    droppedListOpens,
  ])

  /* The other way to settle an unplaceable atom, for a kind that is not
   * listed: decided REJECTED on sight. Left pending with no card and no row, it
   * would hold the record open and keep the banner counting work that cannot
   * be done. A line counts as gone only in an answer that is really there, so
   * a cleared field is not read as a form with no lines. */
  const lostAtoms = useMemo(() => {
    if (!record || !questionnaireResponse) {
      return []
    }
    return record.op.answers.filter((answer) => {
      const reason = unplaceable.get(questionnaireAnswerKey(answer))
      return (
        reason !== undefined &&
        !listsUnplaceable(reason, droppedListOpens) &&
        (reason !== 'lineMissing' ||
          typeof questionnaireResponse.getAnswerValue(answer.linkId) ===
            'string')
      )
    })
  }, [record, questionnaireResponse, unplaceable, droppedListOpens])

  useEffect(() => {
    if (!record || lostAtoms.length === 0) {
      return
    }
    /* Settled through the same single-write path a click takes. That path
     * re-derives the record status after each click; nothing clicks these, so
     * without this the record would stay pending on atoms already decided. */
    decideAtoms(
      lostAtoms.map((answer) => ({
        atomKey: questionnaireAnswerKey(answer),
        status: 'rejected' as const,
      }))
    )
  }, [record, lostAtoms, decideAtoms])

  const accept = useCallback(
    (answer: SuggestedAnswer, editedHtml?: string) => {
      const item = itemsByLinkId.get(answer.linkId)
      if (
        !record ||
        !item ||
        !questionnaireResponse ||
        !hasValue(answer, item)
      ) {
        return
      }
      const atomKey = questionnaireAnswerKey(answer)
      /* An edit that empties the field is treated as no edit rather than as a
       * delete: `setAnswer` removes an answer given a blank string, so the
       * clinician would silently clear text they only meant to redraft. */
      const edited = editedHtml?.trim() ? editedHtml : undefined
      /* Captured inside the mutation rather than around it, so a
       * refetch-on-conflict retry records the answer the write actually landed
       * against instead of the one this render happened to hold. */
      let reversal:
        | { before: AtomAnswerSnapshot; after: AtomAnswerSnapshot }
        | undefined
      // The item's undecided atoms, which decide among themselves who owns a
      // line that more than one of them resolves to.
      const siblings = undecidedByLinkId.get(answer.linkId) ?? [answer]
      mutate(
        {
          instance: questionnaireResponse,
          mutateFn: (qr) => {
            /* Read off the patched instance, which the mutation resolves to the
             * freshest cached copy. */
            const before = snapshotAnswerAtom(
              toAnswerValue(qr.getAnswerValue(answer.linkId)),
              answer
            )
            /* The same question the card surface asks, put to the freshest
             * answer rather than the one the card rendered: a line filled in
             * after the card offered Accept is kept, edited or not. An edit is
             * only reachable from a card, so it gets no pass of its own.
             * Nothing is written, the atom stays pending, and the next render
             * lists it with the suggestions that couldn't be placed. */
            if (
              unplaceableQuestionnaireAtoms(
                questionnaireUrl,
                qr.getAnswerValue(answer.linkId),
                siblings
              ).has(atomKey)
            ) {
              return
            }
            const next = acceptedAnswerValue(
              before.answer,
              answer,
              edited,
              item
            )
            /* Undefined where the value has nowhere to land: `hasValue` above
             * asks only whether the SUGGESTION carries a value this item can
             * take, while a labelled atom also needs the stored answer to hold
             * a line for its label. The check above already turns such an atom
             * away, so this is the backstop for a write it did not foresee.
             * Nothing is written and the atom stays pending, rather than a line
             * being invented for it. */
            if (next === undefined) {
              return
            }
            qr.setAnswer(item, next)
            // Snapshotted from the value written rather than re-read, so an
            // undo compares against exactly what this accept put in the field.
            reversal = { before, after: snapshotAnswerAtom(next, answer) }
          },
        },
        {
          onSuccess: () => {
            // Unset only where the write above refused, which settles nothing.
            if (!reversal) {
              return
            }
            decideAtoms([{ atomKey, status: 'accepted' }])
            recordDecision({
              kind: 'accept',
              questionnaireUrl,
              atomKey,
              answer,
              before: reversal.before,
              after: reversal.after,
            })
          },
        }
      )
    },
    [
      itemsByLinkId,
      undecidedByLinkId,
      questionnaireUrl,
      questionnaireResponse,
      record,
      mutate,
      decideAtoms,
      recordDecision,
    ]
  )

  /* The line is rewritten against the answer the PATCH will see, not the copy
   * this render holds: an accept landing between the click and the commit would
   * shift every index after the line it wrote. A refusal leaves an empty patch,
   * which the client turns into a no-op. */
  const editLine = useCallback(
    (linkId: string, edit: AnswerLineEdit) => {
      const item = questionnaire.item?.find((i) => i.linkId === linkId)
      if (!item || !questionnaireResponse) {
        return
      }
      editMutate({
        instance: questionnaireResponse,
        mutateFn: (qr) => {
          const current = qr.getAnswerValue(linkId)
          const next = substituteLineValue(
            typeof current === 'string' ? current : '',
            edit.index,
            edit.previousHtml,
            toInlineValue(edit.html)
          )
          if (next === null) {
            return
          }
          qr.setAnswer(item, next)
        },
      })
    },
    [questionnaire, questionnaireResponse, editMutate]
  )

  /* Rejecting writes nothing to the response — the stored answer stands
   * untouched and only the decision is recorded, which is why its undo needs no
   * compensating patch. */
  const reject = useCallback(
    (answer: SuggestedAnswer) => {
      if (!record) {
        return
      }
      const atomKey = questionnaireAnswerKey(answer)
      decideAtoms([{ atomKey, status: 'rejected' }])
      recordDecision({
        kind: 'reject',
        questionnaireUrl,
        atomKey,
        answer,
      })
    },
    [record, questionnaireUrl, decideAtoms, recordDecision]
  )

  /* Re-apply a reversed decision. Like the re-pend it reverses, it works on the
   * record holding the live slot, which an undo has just guaranteed holds this
   * atom. The record-level status is RE-DERIVED rather than replayed from what
   * the original decision settled: that record may since have taken on atoms
   * from another run, and settling it around them would take their cards away.
   */
  const redecide = useCallback(
    (entry: QuestionnaireDecisionEntry) => {
      if (!record) {
        return
      }
      const status = entry.kind === 'accept' ? 'accepted' : 'rejected'
      decideAtoms([{ atomKey: entry.atomKey, status }])
    },
    [record, decideAtoms]
  )

  /* Write the compensating answer for an accept. Undo passes from=after,
   * to=before; redo passes them the other way round.
   *
   * The whole decision — including whether to write at all — is planned INSIDE
   * `mutateFn`, against the answer as the patch will see it: planning outside
   * would judge a stale copy, and a refetch-on-conflict retry re-runs only the
   * callback. A refusal therefore leaves an empty patch, which the client turns
   * into a no-op. */
  /* True only when the answer really moved. False covers every other end —
   * refused, failed, nothing mounted to write — so a caller that re-pended the
   * atom for this reversal knows to put that back, rather than leaving a card
   * beside an answer still holding the value it offers.
   *
   * AWAITED, not reported through `mutate`'s per-call callbacks: those belong to
   * the observer that started the mutation, so a questionnaire the clinician
   * leaves mid-write would never commit the entry whose reversal has in fact
   * landed, nor free the claim on it — and the entry would sit at the top of the
   * stack refusing every later press. The Mutation outlives its observer, so the
   * promise settles either way. */
  const reverseAnswer = useCallback(
    async (
      entry: AcceptedAtomEntry,
      from: AtomAnswerSnapshot,
      to: AtomAnswerSnapshot
    ): Promise<boolean> => {
      const item = questionnaire.item?.find(
        (i) => i.linkId === entry.answer.linkId
      )
      if (!item || !questionnaireResponse) {
        return false
      }
      let refusal: string | undefined
      try {
        await reverseMutateAsync({
          instance: questionnaireResponse,
          mutateFn: (qr) => {
            refusal = undefined
            const plan = planAnswerReversal(
              toAnswerValue(qr.getAnswerValue(entry.answer.linkId)),
              from,
              to,
              entry.answer
            )
            if (plan.kind === 'refuse') {
              refusal = plan.reason
              return
            }
            qr.setAnswer(item, plan.value)
          },
        })
      } catch {
        // The mutation's own onError says so; the caller compensates.
        return false
      }
      if (refusal) {
        toast.error(refusal)
        return false
      }
      return true
    },
    [questionnaire, questionnaireResponse, reverseMutateAsync]
  )

  /* Whether a reversal would be REFUSED, asked before anything is written.
   * `planAnswerReversal` is pure and the mutation runs it again against the copy
   * the patch actually sees, so this is a preflight, not the decision: it exists
   * so an undo never re-pends an atom whose reversal it already knows will be
   * turned down, which would leave the card offering a value the clinician's own
   * later edit has replaced. */
  const canReverseAnswer = useCallback(
    (
      entry: AcceptedAtomEntry,
      from: AtomAnswerSnapshot,
      to: AtomAnswerSnapshot
    ): boolean => {
      if (!questionnaireResponse) {
        return false
      }
      const plan = planAnswerReversal(
        toAnswerValue(
          questionnaireResponse.getAnswerValue(entry.answer.linkId)
        ),
        from,
        to,
        entry.answer
      )
      return plan.kind === 'write'
    },
    [questionnaireResponse]
  )

  /* Accept every answer the editor's "Accept all" sweeps, in ONE patch against
   * this whole QuestionnaireResponse.
   *
   * One patch for the resource, not one per item and certainly not one per
   * atom: every item lives in the same record, so several patches would each
   * bump its version and each have to pass its concurrency test against what
   * the previous one just wrote. Run as one pass it is atomic — it applies
   * completely or not at all.
   *
   * The pass runs INSIDE `mutateFn`, against the answers the patch will see, so
   * a refetch-on-conflict retry re-derives rather than replaying a stale plan,
   * and so an item's atoms each substitute into the value the one before it
   * produced.
   *
   * The decisions are NOT recorded on the per-atom undo history: the sweep is
   * one action and comes back in one press, which the editor's batch entry
   * owns. `onDone` reports the writes so that entry can compensate for them,
   * and reports none when the patch failed, so nothing is marked accepted that
   * was never written. */
  const acceptAllAnswers = useCallback(
    (
      answers: readonly SuggestedAnswer[],
      onDone: (result: BulkAcceptResult) => void
    ) => {
      const nothingWritten: BulkAcceptResult = {
        writes: [],
        unplaceableAtomKeys: [],
      }
      if (isReadOnly || !questionnaireResponse) {
        onDone(nothingWritten)
        return
      }
      const writable = answers.filter((answer) =>
        itemsByLinkId.has(answer.linkId)
      )
      let result: BulkAcceptResult = nothingWritten
      mutate(
        {
          instance: questionnaireResponse,
          mutateFn: (qr) => {
            result = applyBulkAccept(
              questionnaireUrl,
              writable,
              (linkId) => itemsByLinkId.get(linkId),
              (linkId) => toAnswerValue(qr.getAnswerValue(linkId)),
              (linkId, value) => {
                const item = itemsByLinkId.get(linkId)
                if (item) {
                  qr.setAnswer(item, value)
                }
              }
            )
          },
        },
        {
          onSuccess: () => onDone(result),
          onError: () => onDone(nothingWritten),
        }
      )
    },
    [isReadOnly, itemsByLinkId, questionnaireUrl, questionnaireResponse, mutate]
  )

  /* The preflight half of the bulk reversal: which items the answer still
   * stands as the sweep left, so the caller can settle the whole batch before
   * writing any of it. Pure, and asked again inside the patch below against the
   * copy it actually sees. */
  const reversibleLinkIds = useCallback(
    (writes: readonly BulkAcceptItemWrite[]): string[] => {
      if (isReadOnly || !questionnaireResponse) {
        return []
      }
      return writes.flatMap((write) => {
        if (!itemsByLinkId.has(write.linkId)) {
          return []
        }
        const plan = planBulkAcceptReversal(
          toAnswerValue(questionnaireResponse.getAnswerValue(write.linkId)),
          write
        )
        return plan.kind === 'write' ? [write.linkId] : []
      })
    },
    [isReadOnly, itemsByLinkId, questionnaireResponse]
  )

  /* Un-write a sweep, item by item, and report which items it managed. An item
   * whose answer has moved since is refused rather than guessed at, exactly as
   * a single atom's reversal is; the caller leaves those atoms accepted, since
   * an atom reading undecided beside its value sitting in the field would
   * describe the plan wrongly. */
  const reverseBulkAccept = useCallback(
    (
      writes: readonly BulkAcceptItemWrite[],
      onDone: (reversedLinkIds: string[]) => void
    ) => {
      if (isReadOnly || !questionnaireResponse) {
        onDone([])
        return
      }
      let reversed: string[] = []
      reverseMutate(
        {
          instance: questionnaireResponse,
          mutateFn: (qr) => {
            reversed = []
            for (const write of writes) {
              const item = itemsByLinkId.get(write.linkId)
              if (!item) {
                continue
              }
              const plan = planBulkAcceptReversal(
                toAnswerValue(qr.getAnswerValue(write.linkId)),
                write
              )
              if (plan.kind === 'refuse') {
                continue
              }
              qr.setAnswer(item, plan.value)
              reversed.push(write.linkId)
            }
          },
        },
        {
          onSuccess: () => onDone(reversed),
          onError: () => onDone([]),
        }
      )
    },
    [isReadOnly, itemsByLinkId, questionnaireResponse, reverseMutate]
  )

  const undoDecision = useCallback(
    (entry: QuestionnaireDecisionEntry) => {
      if (isReadOnly) {
        return
      }
      /* BOTH sides are preflighted before either is written, because the two
       * writes cannot be made atomic and only one order fails safe. An undo may
       * leave a card standing beside text the clinician still has — they can
       * accept it again — but never text removed with no card to put it back. */
      if (
        !canRependQuestionnaireAnswer(entry.questionnaireUrl, entry.atomKey)
      ) {
        toast.error(ANSWER_UNDO_UNAVAILABLE)
        return
      }
      /* One reversal per entry at a time. The entry stays on the stack until
       * its writes have landed, so a second press inside that window would
       * dispatch it again — and two reversals of one decision race, with the
       * compensation of the one that fails putting the decision back on an atom
       * the one that succeeded has already taken out of the answer. */
      if (!claimDecisionReversal(entry.seq)) {
        return
      }
      if (entry.kind === 'reject') {
        // Nothing was written to the response, so there is nothing to reverse.
        void rependQuestionnaireAnswer(
          entry.questionnaireUrl,
          entry.atomKey
        ).then((outcome) => {
          if (outcome.ok) {
            commitUndo(entry.seq)
          }
          releaseDecisionReversal(entry.seq)
        })
        return
      }
      if (!canReverseAnswer(entry, entry.after, entry.before)) {
        releaseDecisionReversal(entry.seq)
        toast.error(ANSWER_CHANGED_REASON)
        return
      }
      /* Staging first, then the answer. A failed staging write leaves the value
       * in the plan exactly as the clinician accepted it; a failed reversal
       * takes the re-pend back through the inverse the outcome carries. */
      void rependQuestionnaireAnswer(
        entry.questionnaireUrl,
        entry.atomKey
      ).then(async (outcome) => {
        if (!outcome.ok) {
          releaseDecisionReversal(entry.seq)
          return
        }
        if (await reverseAnswer(entry, entry.after, entry.before)) {
          commitUndo(entry.seq)
        } else {
          outcome.revert()
        }
        releaseDecisionReversal(entry.seq)
      })
    },
    [
      isReadOnly,
      canRependQuestionnaireAnswer,
      canReverseAnswer,
      rependQuestionnaireAnswer,
      commitUndo,
      reverseAnswer,
    ]
  )

  const redoDecision = useCallback(
    (entry: QuestionnaireDecisionEntry) => {
      if (isReadOnly) {
        return
      }
      /* The mirror of the undo guard, against the state the undo LEFT: the atom
       * is decided on whichever record holds the slot now, which the re-pend's
       * optimistic write has already put in front of this render. */
      const decidable = record?.op.answers.some(
        (answer) => questionnaireAnswerKey(answer) === entry.atomKey
      )
      if (!decidable) {
        toast.error(ANSWER_REDO_UNAVAILABLE)
        return
      }
      if (entry.kind === 'reject') {
        redecide(entry)
        commitRedo(entry.seq)
        return
      }
      if (!canReverseAnswer(entry, entry.before, entry.after)) {
        toast.error(ANSWER_CHANGED_REASON)
        return
      }
      /* The answer first here, the opposite of the undo, for the same reason:
       * the failure this ordering leaves is a card still offering a value that
       * is not in the plan, never a value in the plan with no decision on it. */
      void reverseAnswer(entry, entry.before, entry.after).then((reversed) => {
        if (reversed) {
          redecide(entry)
          commitRedo(entry.seq)
        }
      })
    },
    [isReadOnly, record, canReverseAnswer, redecide, commitRedo, reverseAnswer]
  )

  /* The history is global but only the hook rendering a questionnaire can write
   * its answers, so each one lends the history an executor for its own URL. Held
   * through a ref so the registration survives every re-render and the entry a
   * key press reaches is always applied by the current one. */
  /* The scaffold lines this questionnaire's current answers offer, read from the
   * same response an accept rewrites and enumerated by the same shared model the
   * AI read tool offers the model its options from. */
  const scaffoldLabels = useCallback(
    (linkId: string): AnswerScaffoldLabel[] | undefined => {
      const answer = questionnaireResponse?.getAnswerValue(linkId)
      return typeof answer === 'string'
        ? enumerateAnswerLabels(answer, parseAnswerHtml)
        : undefined
    },
    [questionnaireResponse]
  )
  const executorRef = useRef({
    undo: undoDecision,
    redo: redoDecision,
    acceptAll: acceptAllAnswers,
    reverseBulkAccept,
    reversibleLinkIds,
    scaffoldLabels,
  })
  useEffect(() => {
    executorRef.current = {
      undo: undoDecision,
      redo: redoDecision,
      acceptAll: acceptAllAnswers,
      reverseBulkAccept,
      reversibleLinkIds,
      scaffoldLabels,
    }
  }, [
    undoDecision,
    redoDecision,
    acceptAllAnswers,
    reverseBulkAccept,
    reversibleLinkIds,
    scaffoldLabels,
  ])
  useEffect(
    () =>
      registerQuestionnaireDecisionExecutor(questionnaireUrl, {
        undo: (entry) => executorRef.current.undo(entry),
        redo: (entry) => executorRef.current.redo(entry),
        acceptAll: (answers, onDone) =>
          executorRef.current.acceptAll(answers, onDone),
        reverseBulkAccept: (writes, onDone) =>
          executorRef.current.reverseBulkAccept(writes, onDone),
        reversibleLinkIds: (writes) =>
          executorRef.current.reversibleLinkIds(writes),
        scaffoldLabels: (linkId) => executorRef.current.scaffoldLabels(linkId),
      }),
    [questionnaireUrl]
  )

  /* Dismissing a row of the couldn't-be-placed list is this atom's Reject —
   * the same decision, settlement and undo entry a card's Reject makes — for
   * an atom that has no card to press it on. Resolved against the record this
   * render holds, and a no-op for an atom decided since the row was drawn. */
  const dismissDropped = useCallback(
    (atomKey: string) => {
      const answer = record?.op.answers.find(
        (candidate) => questionnaireAnswerKey(candidate) === atomKey
      )
      if (
        answer &&
        (record?.fieldDecisions?.[atomKey] ?? 'pending') === 'pending'
      ) {
        reject(answer)
      }
    },
    [record, reject]
  )
  const dismissRef = useRef(dismissDropped)
  useEffect(() => {
    dismissRef.current = dismissDropped
  }, [dismissDropped])

  /* Handed to the editor's review bar, which renders the list. Published under
   * an owner of this mount's own, so a second surface rendering the same
   * questionnaire neither clobbers nor withdraws this one's rows. */
  const [publicationOwner] = useState(() => ({}))
  useEffect(() => {
    publishDroppedQuestionnaireAtoms(
      questionnaireUrl,
      publicationOwner,
      droppedRows,
      (atomKey) => dismissRef.current(atomKey)
    )
  }, [questionnaireUrl, publicationOwner, droppedRows])
  useEffect(
    () => () =>
      withdrawDroppedQuestionnaireAtoms(questionnaireUrl, publicationOwner),
    [questionnaireUrl, publicationOwner]
  )

  return {
    answersByLinkId,
    /* Streamed cards show but cannot be decided: the decision is persisted
     * against the staged record, which the completing call has not written yet.
     * It is the same block the page already applies while it is still creating
     * the response, and it is what keeps an aborted run's cards from reaching
     * the record. */
    canAccept: Boolean(questionnaireResponse) && Boolean(record),
    accept,
    reject,
    editLine,
  }
}
